import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, Play, Pause } from "lucide-react";
import { useLang } from "../context/LangContext";

const AUTOPLAY_MS = 3500;

export default function Lightbox({ items, openIndex, onClose }) {
  const { t } = useLang();
  const [index, setIndex] = useState(openIndex ?? 0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  // Sync when openIndex changes (re-open at clicked tile)
  useEffect(() => {
    if (openIndex !== null && openIndex !== undefined) {
      setIndex(openIndex);
    }
  }, [openIndex]);

  const total = items?.length || 0;
  const isOpen = openIndex !== null && openIndex !== undefined && total > 0;

  const goTo = useCallback(
    (next) => {
      if (total === 0) return;
      const n = ((next % total) + total) % total;
      setIndex(n);
    },
    [total]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose();
      else if (e.key === " " || e.key === "k") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, next, prev, onClose]);

  // Autoplay timer
  useEffect(() => {
    if (!isOpen || !playing) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = setInterval(() => {
      setIndex((i) => (total > 0 ? (i + 1) % total : 0));
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, playing, total]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Touch swipe support
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 60) {
      if (delta < 0) next();
      else prev();
    }
    touchStart.current = null;
  };

  if (!isOpen) return null;

  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: "rgba(8, 14, 11, 0.94)" }}
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-between p-4 md:p-6 text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-sm tracking-widest opacity-80" data-testid="lightbox-counter">
          {String(index + 1).padStart(2, "0")} <span className="opacity-50 mx-1">{t("image_of")}</span> {String(total).padStart(2, "0")}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{
              background: playing ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
            aria-label={playing ? t("pause") : t("play")}
            title={playing ? t("pause") : t("play")}
            data-testid={playing ? "lightbox-pause-btn" : "lightbox-play-btn"}
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
            aria-label={t("close")}
            title={t("close")}
            data-testid="lightbox-close-btn"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Image stage */}
      <div
        className="relative max-w-[92vw] max-h-[78vh] w-full flex items-center justify-center px-12 md:px-20"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={item.url}
          src={item.url}
          alt={item.alt || item.caption || "gallery image"}
          className="max-w-full max-h-[78vh] object-contain rounded-2xl select-none"
          style={{
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
          }}
          decoding="async"
          draggable={false}
          data-testid="lightbox-image"
        />

        {/* Prev */}
        <button
          type="button"
          onClick={prev}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
          style={{
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.20)",
            backdropFilter: "blur(6px)",
          }}
          aria-label={t("prev")}
          data-testid="lightbox-prev-btn"
        >
          <ChevronLeft size={20} />
        </button>
        {/* Next */}
        <button
          type="button"
          onClick={next}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
          style={{
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.20)",
            backdropFilter: "blur(6px)",
          }}
          aria-label={t("next")}
          data-testid="lightbox-next-btn"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Caption + autoplay progress */}
      <div
        className="absolute bottom-0 inset-x-0 px-6 md:px-10 pb-6 md:pb-8 pt-10"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-3xl mx-auto text-center text-white/90">
          {item.caption && (
            <p className="font-serif text-xl md:text-2xl leading-snug" data-testid="lightbox-caption">
              {item.caption}
            </p>
          )}
          {/* Thumbnail strip */}
          <div className="mt-5 flex items-center justify-center gap-2 overflow-x-auto pb-1" data-testid="lightbox-thumbs">
            {items.map((it, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                className="shrink-0 overflow-hidden rounded-md transition-all"
                style={{
                  width: 56,
                  height: 38,
                  outline: i === index ? "2px solid white" : "2px solid transparent",
                  outlineOffset: 2,
                  opacity: i === index ? 1 : 0.55,
                }}
                aria-label={`Go to ${i + 1}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={it.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          {/* Autoplay progress bar */}
          <div
            className="mt-4 h-[2px] w-32 mx-auto overflow-hidden rounded-full"
            style={{ background: "rgba(255,255,255,0.15)" }}
            aria-hidden="true"
          >
            <div
              key={`${index}-${playing}`}
              className="h-full"
              style={{
                background: "rgba(255,255,255,0.85)",
                width: "100%",
                transformOrigin: "left center",
                transform: "scaleX(0)",
                animation: playing ? `lbProgress ${AUTOPLAY_MS}ms linear forwards` : "none",
              }}
            />
          </div>
        </div>
      </div>

      {/* keyframes injected here for portability */}
      <style>{`@keyframes lbProgress { from { transform: scaleX(0); } to { transform: scaleX(1); } }`}</style>
    </div>
  );
}
